import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, useSchema } from '../../utils/seo';

export default function Contact() {
  useSEO({
    title: 'Kontakt & Impressum - EarnProof | Christopher Humpert',
    description: 'Kontaktiere EarnProof für Fragen, Feedback oder Zusammenarbeit. E-Mail: hello@earnproof.org, Telefon: 0208 77800206. Impressum und rechtliche Informationen.',
    keywords: 'earnproof kontakt, impressum, christopher humpert kontakt, app review anfrage',
    canonical: 'https://example.com/contact',
  });

  useSchema({
    type: 'ContactPage',
    data: {
      name: 'Kontakt & Impressum',
      description: 'Kontaktmöglichkeiten und Impressum von EarnProof',
      url: 'https://example.com/contact',
      mainEntity: {
        '@type': 'Organization',
        name: 'EarnProof',
        email: 'hello@earnproof.org',
        telephone: '+49-208-77800206',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Gutenbergstraße 34',
          addressLocality: 'Oberhausen',
          postalCode: '46045',
          addressCountry: 'DE',
        },
      },
    },
  });

  useSchema({
    type: 'FAQPage',
    data: {
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Wie werden die Apps getestet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Jede App wird mindestens 30 Tage lang täglich genutzt. Ich dokumentiere alle Einnahmen, Auszahlungen und Probleme mit Screenshots und Belegen.',
          },
        },
        {
          '@type': 'Question',
          name: 'Verdienst du Geld mit Affiliate-Links?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'EarnProof ist unabhängig und werbefrei. Wenn du dich über meine Links anmeldest, unterstützt du das Projekt – ohne Mehrkosten für dich. In vielen Fällen erhältst du über den Affiliate-Link sogar einen kleinen Bonus.',
          },
        },
        {
          '@type': 'Question',
          name: 'Kann ich eigene App-Erfahrungen teilen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolut! Ich freue mich über Community-Feedback und eigene Erfahrungsberichte. Schreib mir einfach eine E-Mail oder Nachricht.',
          },
        },
        {
          '@type': 'Question',
          name: 'Wie oft werden neue Apps getestet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Ich teste kontinuierlich neue Apps. Etwa 2-3 neue Reviews pro Monat, plus Updates zu bestehenden Apps bei Änderungen.',
          },
        },
        {
          '@type': 'Question',
          name: 'Sind die Einnahmen-Screenshots echt?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Ja, alle Screenshots sind unbearbeitet und echt. Bei Bedarf kann ich zusätzliche Belege wie Kontoauszüge oder Steuerdokumente bereitstellen.',
          },
        },
      ],
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactMethods = [
    {
      icon: 'ri-mail-line',
      title: 'E-Mail',
      description: 'Für allgemeine Anfragen und Feedback',
      contact: 'hello@earnproof.org',
      action: 'E-Mail senden'
    },
    {
      icon: 'ri-telegram-line',
      title: 'Telegram',
      description: 'Schnelle Antworten und Community-Chat',
      contact: '@EarnProofSupport',
      action: 'Telegram öffnen'
    }
  ];

  const faqItems = [
    {
      question: 'Wie werden die Apps getestet?',
      answer: 'Jede App wird mindestens 30 Tage lang täglich genutzt. Ich dokumentiere alle Einnahmen, Auszahlungen und Probleme mit Screenshots und Belegen.'
    },
    {
      question: 'Verdienst du Geld mit Affiliate-Links?',
      answer: 'EarnProof ist unabhängig und werbefrei. Wenn du dich über meine Links anmeldest, unterstützt du das Projekt – ohne Mehrkosten für dich. In vielen Fällen erhältst du über den Affiliate-Link sogar einen kleinen Bonus oder Startvorteil in der jeweiligen App. So profitieren beide Seiten – du bekommst mehr, und ich kann neue Plattformen weiter testen und hier teilen.'
    },
    {
      question: 'Kann ich eigene App-Erfahrungen teilen?',
      answer: 'Absolut! Ich freue mich über Community-Feedback und eigene Erfahrungsberichte. Schreib mir einfach eine E-Mail oder Nachricht.'
    },
    {
      question: 'Wie oft werden neue Apps getestet?',
      answer: 'Ich teste kontinuierlich neue Apps. Etwa 2-3 neue Reviews pro Monat, plus Updates zu bestehenden Apps bei Änderungen.'
    },
    {
      question: 'Sind die Einnahmen-Screenshots echt?',
      answer: 'Ja, alle Screenshots sind unbearbeitet und echt. Bei Bedarf kann ich zusätzliche Belege wie Kontoauszüge oder Steuerdokumente bereitstellen.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-cyan-900/20"></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Kontakt & Impressum
            <span className="block text-2xl md:text-3xl text-teal-400 font-normal mt-2">
              Lass uns ins Gespräch kommen
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Fragen, Feedback oder eigene Erfahrungen? Ich freue mich über jede Nachricht und antworte meist innerhalb von 24 Stunden.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Kontaktmöglichkeiten</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-6 text-center hover:border-teal-700/50 transition-colors">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-mail-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">E-Mail</h3>
              <p className="text-gray-300 mb-4">Für allgemeine Anfragen und Feedback</p>
              <div className="text-teal-400 font-medium mb-4">hello@earnproof.org</div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.open('mailto:hello@earnproof.org')}
              >
                E-Mail senden
              </Button>
            </div>

            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-6 text-center hover:border-teal-700/50 transition-colors">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-telegram-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Telegram</h3>
              <p className="text-gray-300 mb-4">Schnelle Antworten und Community-Chat</p>
              <div className="text-teal-400 font-medium mb-4">@EarnProofSupport</div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.open('https://t.me/EarnProofSupport')}
              >
                Telegram öffnen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Nachricht senden</h2>
            <p className="text-gray-300">
              Nutze das Formular für detaillierte Anfragen oder längere Nachrichten.
            </p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    placeholder="Dein Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    placeholder="deine@email.de"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                  Betreff *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-400 transition-colors text-sm pr-8"
                >
                  <option value="">Betreff wählen</option>
                  <option value="app-suggestion">App-Vorschlag</option>
                  <option value="feedback">Feedback zur Website</option>
                  <option value="collaboration">Zusammenarbeit</option>
                  <option value="bug-report">Fehler melden</option>
                  <option value="general">Allgemeine Anfrage</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm resize-none"
                  placeholder="Deine Nachricht... (max. 500 Zeichen)"
                />
                <div className="text-right text-xs text-gray-400 mt-1">
                  {formData.message.length}/500 Zeichen
                </div>
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 text-green-400">
                  <i className="ri-check-circle-line mr-2"></i>
                  Nachricht erfolgreich gesendet! Ich melde mich bald bei dir.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-400">
                  <i className="ri-error-warning-line mr-2"></i>
                  Fehler beim Senden. Bitte versuche es erneut oder nutze eine andere Kontaktmöglichkeit.
                </div>
              )}
              
              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Nachricht senden
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Häufige Fragen</h2>
          <div className="space-y-6">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">{item.question}</h3>
                <p className="text-gray-300">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Impressum</h2>
          <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Angaben gemäß § 5 TMG</h3>
                <div className="space-y-2 text-gray-300">
                  <p><strong>Betreiber:</strong> Christopher Humpert</p>
                  <p><strong>Adresse:</strong> Gutenbergstraße 34<br />46045 Oberhausen</p>
                  <p><strong>E-Mail:</strong> hello@earnproof.org</p>
                  <p><strong>Telefon:</strong> 0208 77800206</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Rechtliche Hinweise</h3>
                <div className="space-y-2 text-gray-300">
                  <p><strong>Haftungsausschluss:</strong> Alle Angaben ohne Gewähr. Earning-Apps können Risiken bergen.</p>
                  <p><strong>Datenschutz:</strong> Wir erheben nur notwendige Daten und geben diese nicht weiter.</p>
                  <p><strong>Urheberrecht:</strong> Alle Inhalte sind urheberrechtlich geschützt.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
